import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ContentNugget, NUGGET_VARIANTS, PriorityPlaylist } from '@backrs_ui/common-ui';

import { handlePanelNuggetClick } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { useDelayedLoadingState } from '@backrs_ui/grow-ui/src/hooks/useDelayedLoadingState';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { openSuggestedPlaylistViewStep } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';

import { SearchPage } from './SearchPage';
import { useAgentContext } from './SearchPageContainer.agentContext';
import { usePagination } from '../hooks/usePagination';
import { useSearchActions } from '../hooks/useSearchActions';
import { useSearchPageCategoryFilterMap } from '../hooks/useSearchPageCategoryFilterMap';
import { SEARCH_PAGE_CATEGORIES, SearchPageCategory, SearchCategory } from '../search.types';

export const SearchPageContainer = () => {
  const { subPageCategory } = useParams();
  const [urlSearchParams] = useSearchParams();
  // Ensure category is a valid SearchPageCategory
  const currentPageCategory = (subPageCategory ||
    SEARCH_PAGE_CATEGORIES.DEFAULT) as SearchPageCategory;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const searchState = useAppSelector((state) => state.search);
  const searchParams = searchState.pageSearchParams[currentPageCategory];
  const currentSearchTerm = searchParams?.searchTerm || '';
  const selectedFilters = searchParams?.selectedFilters || [];
  const searchResults = searchParams?.searchResults;

  const { careerPaths, industries, careerCategories } = useAppSelector((state) => state.careerPath);

  // get categories map, like what categories are available for the current search page
  const searchPageCategoryFilterMap = useSearchPageCategoryFilterMap({
    careerPaths,
    industries,
    careerCategories,
  });

  // get pagination controls
  const { handleNextPage, handlePrevPage, handleSelectPage, clearSearch } = usePagination({
    currentSearchTerm,
    selectedFilters,
    currentPageCategory,
    currentPage: searchResults?.page || 1,
  });

  const { handleSettingSelectedItems, handleUpdateSearchTerm, initiateSearch } = useSearchActions({
    currentPageCategory,
    searchParams,
    currentSearchTerm,
    selectedFilters,
    hasInitialSearchBeenMade: searchParams?.hasInitialSearchBeenMade,
  });

  useEffect(() => {
    // whenever we initially land on a search page, we initiate a search just once to get the initial filtered and alphabetized results.
    // For the default search, we don't make an initial search (at this point)
    if (currentPageCategory === SEARCH_PAGE_CATEGORIES.DEFAULT) {
      return;
    }

    const isInternshipsPage = currentPageCategory === SEARCH_PAGE_CATEGORIES.INTERNSHIPS;

    // Support both single (career-path) and multiple (career-paths) URL params
    const singleCareerPathId = urlSearchParams.get('career-path');
    const multipleCareerPathIds = urlSearchParams.get('career-paths');

    const getCareerPathFilterKeys = (): SearchCategory[] => {
      if (!isInternshipsPage) return [];
      if (multipleCareerPathIds) {
        return multipleCareerPathIds
          .split(',')
          .map((id) => `INTERNSHIP_CAREER_PATH_${id}` as SearchCategory);
      }
      if (singleCareerPathId) {
        return [`INTERNSHIP_CAREER_PATH_${singleCareerPathId}` as SearchCategory];
      }

      return [];
    };

    const careerPathFilterKeys = getCareerPathFilterKeys();

    const hasCareerPathParam = careerPathFilterKeys.length > 0;
    const hasInitialSearchBeenMade = searchParams?.hasInitialSearchBeenMade;

    // initial page load
    if (!hasInitialSearchBeenMade) {
      if (hasCareerPathParam) {
        handleSettingSelectedItems(careerPathFilterKeys);
      } else {
        initiateSearch();
      }

      return;
    }

    // if the career path filter is different, we update the selected filters on mount
    if (hasCareerPathParam) {
      const currentCareerPathFilters = selectedFilters.filter((filter) =>
        filter.startsWith('INTERNSHIP_CAREER_PATH_'),
      );

      const filtersMatch =
        currentCareerPathFilters.length === careerPathFilterKeys.length &&
        careerPathFilterKeys.every((key) => currentCareerPathFilters.includes(key));

      if (!filtersMatch) {
        handleSettingSelectedItems(careerPathFilterKeys);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageCategory, urlSearchParams]);

  const handlePanelNuggetClickWrapper = (nugget: ContentNugget) => {
    if (nugget.variant === NUGGET_VARIANTS.PLAYLIST && nugget.playlist_info) {
      dispatch(openSuggestedPlaylistViewStep(nugget.playlist_info as PriorityPlaylist));

      return;
    }

    handlePanelNuggetClick(nugget, navigate);
  };

  const categories =
    searchPageCategoryFilterMap[currentPageCategory || SEARCH_PAGE_CATEGORIES.DEFAULT] ||
    searchPageCategoryFilterMap[SEARCH_PAGE_CATEGORIES.DEFAULT];

  const totalPages = Math.ceil((searchResults?.total || 1) / (searchResults?.perPage || 1));

  // Delay surfacing the loading state so fast responses don't flash the spinner.
  const isSearching = useDelayedLoadingState(searchState.loading);

  useAgentContext({
    currentPageCategory,
    currentSearchTerm,
    selectedFilters,
    resultCount: searchResults?.total ?? null,
    currentPage: searchResults?.page || 1,
    totalPages,
    isSearching,
    availableFilterGroups: categories,
    searchResults,
  });

  return (
    <SearchPage
      categories={categories}
      searchResults={searchResults}
      isSearching={isSearching}
      currentSearchTerm={currentSearchTerm}
      selectedItems={selectedFilters}
      currentPageCategory={currentPageCategory}
      handlePanelNuggetClick={handlePanelNuggetClickWrapper}
      setSelectedItems={handleSettingSelectedItems}
      handleUpdateSearchTerm={handleUpdateSearchTerm}
      handleNextPage={handleNextPage}
      handlePrevPage={handlePrevPage}
      initiateSearch={initiateSearch}
      clearSearch={clearSearch}
      handlePageChange={handleSelectPage}
    />
  );
};
